import React, { useRef, useState } from 'react';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { pageSamples, pageTypes } from '../data/content';
const controlClass = 'inline-flex h-12 w-12 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors duration-150 ease-out hover:bg-ink hover:text-paper focus:outline-none focus-visible:ring-4 focus-visible:ring-pen/30 disabled:pointer-events-none disabled:opacity-35';
function pad(value: number): string {
  return value.toString().padStart(2, '0');
}
export function InsidePages() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState<number>(0);
  const total = pageSamples.length;
  const goTo = (next: number) => {
    const track = trackRef.current;
    if (!track) return;
    const clamped = Math.max(0, Math.min(total - 1, next));
    track.scrollTo({
      left: clamped * track.clientWidth,
      behavior: 'smooth'
    });
    setIndex(clamped);
  };
  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    const current = Math.round(track.scrollLeft / track.clientWidth);
    if (current !== index) setIndex(current);
  };
  return <section id="inside-pages" className="w-full border-y border-ink/10 bg-paper-deep px-8 py-24 sm:px-14 sm:py-32" data-id="element-135" data-mp="1dukfjouvqpms">
      <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-12 lg:items-center" data-id="element-136" data-mp="1hu2yif354qmz">
        <div className="lg:col-span-5" data-id="element-137" data-mp="13v3an9ngcfrv">
          <p className="font-hand text-3xl leading-none text-pen" data-id="element-138" data-mp="13y7nevezgju5">Feuilletez le matériel</p>
          <h2 className="mt-4 font-display text-[2.6rem] leading-[1.02] tracking-tight text-ink sm:text-6xl" data-id="element-139" data-mp="18sv2k3o3k1w2">
            Découvrez les pages <em className="text-pen" data-id="element-140" data-mp="ywi7b1cwlfz7">de l’intérieur.</em>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-ink/75" data-id="element-141" data-mp="yyur7k2820ew">
            Des pages réelles du pack, des premières découvertes au CP aux activités
            d’approfondissement du CM2 et au laboratoire transversal.
          </p>

          <div className="mt-10 flex items-center gap-5" data-id="element-142" data-mp="62v9woad9db6">
            <button type="button" onClick={() => goTo(index - 1)} disabled={index === 0} aria-label="Page précédente" className={controlClass} data-id="element-143" data-mp="6091kmfnitrw">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-144" data-mp="3o889a8seuin3" />
            </button>
            <p className="font-display text-2xl tabular-nums text-ink" aria-live="polite" data-id="element-145" data-mp="ostoln8o64ns">
              {pad(index + 1)}
              <span className="text-ink/40" data-id="element-146" data-mp="kqzsls9f5oty"> / {pad(total)}</span>
            </p>
            <button type="button" onClick={() => goTo(index + 1)} disabled={index === total - 1} aria-label="Page suivante" className={controlClass} data-id="element-147" data-mp="g09o0h8thq0b">
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-148" data-mp="18hvpv4wn9c8l" />
            </button>
          </div>

          <p className="mt-8 border-t border-ink/15 pt-5 text-sm text-ink/70" data-id="element-149" data-mp="13pc5np3gbiut">
            {pageTypes.join(' · ')}
          </p>
        </div>

        <div className="lg:col-span-7" data-id="element-150" data-mp="1g0bs3w2a4jez">
          <div ref={trackRef} onScroll={handleScroll} role="group" aria-roledescription="carrousel" aria-label="Pages d'exemple du matériel" tabIndex={0} onKeyDown={event => {
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            goTo(index + 1);
          }
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            goTo(index - 1);
          }
        }} className="flex w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-2xl [-ms-overflow-style:none] [scrollbar-width:none] focus:outline-none focus-visible:ring-4 focus-visible:ring-pen/30 [&::-webkit-scrollbar]:hidden" data-id="element-151" data-mp="1g1eim2vowjud">
            {pageSamples.map((page, pageIndex) => <article key={page.level} aria-roledescription="diapositive" aria-label={`${pageIndex + 1} sur ${total}`} className="w-full min-w-0 shrink-0 snap-start px-1 pb-8 pt-1" data-id="element-152" data-mp="242ykh8otxhj3" data-mpi={pageIndex}>
                {page.src ? <img src={page.src} alt={`${page.title} — page d'exemple, ${page.level}`} loading={pageIndex === 0 ? 'eager' : 'lazy'} className="mx-auto block h-auto w-full max-w-md rounded-lg bg-white shadow-[0_24px_40px_-24px_rgba(20,32,61,0.45)]" data-id="element-153" data-mp="28toqmo52xoeu" /> : <div className="relative mx-auto flex aspect-[4/5] w-full max-w-md flex-col justify-between overflow-hidden rounded-lg border border-ink/10 bg-seyes p-8 pl-14 shadow-[0_24px_40px_-24px_rgba(20,32,61,0.45)] sm:p-10 sm:pl-16" data-id="element-154" data-mp="1v078n7s1kjqx">
                    <span aria-hidden="true" className="absolute inset-y-0 left-8 w-px bg-margin sm:left-10" data-id="element-155" data-mp="31ivrhts0aq39" />
                    <p className="font-hand text-2xl text-pen" data-id="element-156" data-mp="nabk6gfrpa1k">{page.level}</p>
                    <div data-id="element-157" data-mp="s9fxnj9q5txp">
                      <h3 className="font-display text-3xl leading-tight text-ink sm:text-4xl" data-id="element-158" data-mp="saqwd4obcbhy">
                        {page.title}
                      </h3>
                      {page.caption && <p className="mt-3 text-sm leading-relaxed text-ink/70" data-id="element-159" data-mp="ja18cn5anpes">{page.caption}</p>}
                    </div>
                    <p className="text-xs font-medium text-ink/60" data-id="element-160" data-mp="170y9d4vjx2b9">SCIENCES PRÊTES™</p>
                  </div>}
              </article>)}
          </div>

          <div className="flex justify-center gap-2" data-id="element-161" data-mp="x8ir06gcebpu">
            {pageSamples.map((page, dotIndex) => <button key={page.level} type="button" onClick={() => goTo(dotIndex)} aria-label={`Aller à la page ${dotIndex + 1}`} aria-current={dotIndex === index} className="flex h-6 items-center px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-pen/40" data-id="element-162" data-mp="125r8e4to2bq1" data-mpi={dotIndex}>
                <span className={`block h-1.5 w-6 rounded-full transition-colors duration-150 ease-out ${dotIndex === index ? 'bg-ink' : 'bg-ink/20'}`} data-id="element-163" data-mp="1zltu9ndkfn0f" />
              </button>)}
          </div>
        </div>
      </div>
    </section>;
}